import { useState } from "react";
import { DAYS, TIMES, STATUS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { ChevronDown, Lightbulb } from "lucide-react";

// A pre-filled example the parent can look at before compiling.
// Represents a realistic mixed week.
const EXAMPLE = {
  "lun_14:30": "green",  "lun_15:00": "green",  "lun_15:30": "green",  "lun_16:00": "green",
  "lun_16:30": "green",  "lun_17:00": "green",  "lun_17:30": "green",  "lun_18:00": "green",
  "lun_18:30": "green",  "lun_19:00": "green",

  "mar_14:30": "red",    "mar_15:00": "red",    "mar_15:30": "red",    "mar_16:00": "red",
  "mar_16:30": "red",    "mar_17:00": "red",    "mar_17:30": "red",    "mar_18:00": "red",
  "mar_18:30": "red",    "mar_19:00": "red",

  "mer_14:30": "red",    "mer_15:00": "red",    "mer_15:30": "red",    "mer_16:00": "yellow",
  "mer_16:30": "green",  "mer_17:00": "green",  "mer_17:30": "green",  "mer_18:00": "green",
  "mer_18:30": "green",  "mer_19:00": "green",

  "gio_14:30": "red",    "gio_15:00": "red",    "gio_15:30": "red",    "gio_16:00": "yellow",
  "gio_16:30": "yellow", "gio_17:00": "green",  "gio_17:30": "green",  "gio_18:00": "green",
  "gio_18:30": "green",  "gio_19:00": "green",

  "ven_14:30": "red",    "ven_15:00": "yellow", "ven_15:30": "yellow", "ven_16:00": "green",
  "ven_16:30": "green",  "ven_17:00": "green",  "ven_17:30": "green",  "ven_18:00": "green",
  "ven_18:30": "red",    "ven_19:00": "red",
};

export default function ExampleGrid() {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="rounded-xl border border-amber-200 bg-amber-50/60 overflow-hidden"
      data-testid="example-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="example-toggle"
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-amber-100/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-semibold text-amber-900">
            Non sai come compilare? Vedi un esempio
          </span>
        </div>
        <ChevronDown
          className={cn(
            "w-4 h-4 text-amber-700 transition-transform",
            open && "rotate-180"
          )}
        />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1">
          <p className="text-xs text-amber-800 mb-3 leading-relaxed">
            Esempio: <strong>Lun</strong> sempre disponibile;{" "}
            <strong>Mar</strong> mai disponibile (altro sport e rientro);{" "}
            <strong>Mer</strong> non si riesce fino alle 16, meglio dalle 16:30;{" "}
            <strong>Gio</strong> non riusciamo fino alle 16, sarebbe meglio dalle 17;{" "}
            <strong>Ven</strong> non riusciamo prima delle 15, sarebbe meglio dalle 16 e finire massimo alle 18:30.
          </p>

          {/* Mini grid */}
          <div className="rounded-lg bg-white border border-amber-200 p-3 overflow-x-auto">
            <div className="min-w-[420px]">
              {/* Days header */}
              <div className="grid grid-cols-[46px_repeat(5,minmax(0,1fr))] gap-1 mb-1.5">
                <div />
                {DAYS.map((d) => (
                  <div
                    key={d.key}
                    className="text-center text-[10px] font-bold uppercase tracking-wider text-slate-600"
                  >
                    {d.short}
                  </div>
                ))}
              </div>

              {TIMES.map((t) => (
                <div
                  key={t}
                  className="grid grid-cols-[46px_repeat(5,minmax(0,1fr))] gap-1 mb-1 items-center"
                >
                  <div className="text-[10px] font-mono font-semibold text-slate-500 text-right pr-1.5">
                    {t}
                  </div>
                  {DAYS.map((d) => {
                    const s = EXAMPLE[`${d.key}_${t}`];
                    return (
                      <div
                        key={d.key + t}
                        className={cn(
                          "h-4 rounded",
                          s ? STATUS[s].bg : "bg-slate-100"
                        )}
                        title={s ? STATUS[s].label : "non indicato"}
                      />
                    );
                  })}
                </div>
              ))}

              {/* Legend */}
              <div className="flex flex-wrap gap-3 mt-3 text-[10px] text-slate-600">
                <div className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded bg-emerald-500" />
                  Disponibile
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded bg-amber-400" />
                  Scomodo
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded bg-rose-500" />
                  Impossibile
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
